/**
 * Category Constants - Single Source of Truth
 * Use these for dropdowns, forms, and database storage
 */

/**
 * Category Constants - Single Source of Truth
 * Use these for dropdowns, forms, and database storage
 *
 * `aliases` — extra search keywords (EN + AR). NOT shown in any UI;
 * consumed by `utils/optionSearch.js` so a user typing "phone",
 * "mobile", "موبايل", or "هاتف" finds Electronics & Accessories
 * even if that exact word doesn't appear in the visible label.
 * Add words conservatively — every alias broadens what matches.
 */

export const categories = [
  { key: "vehicles", ar: "المركبات والدراجات النارية", en: "Vehicles & Motorbikes",
    aliases: { en: ["car","cars","auto","automobile","vehicle","motorbike","motorcycle","bike","scooter","truck","pickup","van","bus"],
               ar: ["سياره","عربيه","عربه","موتر","موتور","دراجه","دراجه ناريه","عجله","شاحنه","بكب","فان","حافله","باص"] } },
  { key: "spare_parts", ar: "قطع غيار وإكسسوارات المركبات", en: "Vehicle Spare Parts & Accessories",
    aliases: { en: ["tire","tyre","battery","brake","engine","oil","filter","wheel","spare","parts"],
               ar: ["كاوتش","اطار","بطاريه","فرامل","محرك","زيت","فلتر","كفر","قطع غيار","قطع","غيار"] } },
  // Specific electronics buckets are listed BEFORE the generic
  // "Electronics & Accessories" parent so that:
  //  (a) the unfiltered dropdown surfaces the more-actionable choice first
  //      (Mobile / Laptops show before Electronics — both are real tap
  //      targets, but the specific ones are the more common picks)
  //  (b) when a search query scores equally on `electronics` (alias hit)
  //      and on `mobile_accessories` / `laptops_accessories` (alias hit),
  //      the relevance ranker's stable tie-break favours the specific
  //      child here. e.g. typing "phone" → Mobile & Accessories wins.
  // Keys are unchanged — DB schema is unaffected.
  { key: "mobile_accessories", ar: "الموبايلات والإكسسوارات", en: "Mobile & Accessories",
    aliases: { en: ["phone","phones","mobile","mobiles","smartphone","cell","cellphone","case","cover","screen protector","charger","cable"],
               ar: ["موبايل","موبايلات","هاتف","هواتف","جوال","تلفون","سمارت","كفر","جراب","واقي","شاحن","كيبل","سلك"] } },
  { key: "laptops_accessories", ar: "اللابتوبات والإكسسوارات", en: "Laptops & Accessories",
    aliases: { en: ["laptop","laptops","notebook","macbook","computer","pc","desktop","keyboard","mouse"],
               ar: ["لابتوب","لاب توب","كمبيوتر","حاسوب","ماك","كيبورد","لوحه مفاتيح","ماوس","فاره"] } },
  { key: "electronics", ar: "الإلكترونيات والإكسسوارات", en: "Electronics & Accessories",
    aliases: { en: ["phone","phones","mobile","mobiles","smartphone","cell","cellphone","laptop","laptops","computer","pc","tablet","ipad","charger","headphones","earphones","headset","tv","television","camera","speaker","watch","smartwatch","gadget","electronic","electronics"],
               ar: ["جهاز","اجهزه","هاتف","هواتف","موبايل","موبايلات","جوال","جوالات","تلفون","تليفون","سمارت","لابتوب","كمبيوتر","حاسوب","حاسبه","تابلت","ايباد","شاحن","شواحن","سماعه","سماعات","تلفزيون","تلفزيونات","شاشه","كاميرا","ساعه","ساعات","الكترون","الكترونيات"] } },
  { key: "industrial", ar: "الآلات الصناعية", en: "Industrial Machinery",
    aliases: { en: ["industrial","factory","machine","machinery"], ar: ["صناعي","مصنع","ماكينه","ماكينات","الات"] } },
  { key: "equipment", ar: "المعدات والأدوات", en: "Equipment & Tools",
    aliases: { en: ["tool","tools","drill","hammer","equipment"], ar: ["اداه","ادوات","معدات","مفك","مطرقه","شاكوش","دريل"] } },
  { key: "real_estate", ar: "العقارات", en: "Real Estate",
    aliases: { en: ["house","home","apartment","flat","villa","rent","rental","sale","land","plot","real","estate","property","studio","office space"],
               ar: ["عقار","عقارات","شقه","شقق","بيت","منزل","فيلا","ايجار","للايجار","للبيع","ارض","اراضي","مكتب","استوديو","عماره","عمارات"] } },
  { key: "agriculture", ar: "الزراعة", en: "Agriculture",
    aliases: { en: ["agriculture","farm","farming","crop","tractor","seeds","fertilizer"], ar: ["زراعه","مزرعه","محصول","تراكتور","بذور","سماد","فلاحه"] } },
  { key: "food", ar: "الأغذية والمشروبات", en: "Food & Beverages",
    aliases: { en: ["food","drink","drinks","beverage","beverages","grocery","groceries","snack"], ar: ["اكل","طعام","مأكولات","مشروب","مشروبات","بقاله","اغذيه","غذاء"] } },
  { key: "jewelry", ar: "المجوهرات", en: "Jewelry",
    aliases: { en: ["gold","silver","ring","rings","chain","necklace","bracelet","earring","jewel","jewelry","jewellery","diamond"],
               ar: ["ذهب","فضه","خاتم","خواتم","سلسله","قلاده","سوار","حلق","حلقان","مجوهرات","الماس","الماظ"] } },
  { key: "gifts", ar: "الهدايا والحرف", en: "Gifts & Crafts",
    aliases: { en: ["gift","gifts","craft","crafts","handmade","present"], ar: ["هديه","هدايا","حرف","يدويه","حرفه"] } },
  { key: "pets", ar: "الحيوانات الأليفة والماشية", en: "Pets & Cattle",
    aliases: { en: ["pet","pets","dog","cat","cattle","cow","sheep","goat","bird","horse","livestock"],
               ar: ["حيوان","حيوانات","كلب","قطه","ابقار","بقره","غنم","ماعز","طير","طيور","حصان","ماشيه"] } },
  { key: "metals", ar: "المعادن والسبائك", en: "Metals & Alloys",
    aliases: { en: ["metal","metals","alloy","steel","copper","iron","aluminum"], ar: ["معدن","معادن","سبائك","حديد","نحاس","المنيوم","فولاذ"] } },
  { key: "fabrics", ar: "الأقمشة والمواد الخام", en: "Fabrics & Raw Materials",
    aliases: { en: ["fabric","fabrics","cloth","textile","cotton","silk","raw"], ar: ["قماش","اقمشه","نسيج","قطن","حرير","مواد خام"] } },
  { key: "garden", ar: "الحدائق والديكور", en: "Garden & Decorations",
    aliases: { en: ["garden","plant","plants","flower","flowers","decor","decoration"], ar: ["حديقه","حدائق","نبات","نباتات","زهره","زهور","ديكور","زراعه"] } },
  { key: "it", ar: "معدات تقنية المعلومات وخدمات البرمجيات", en: "IT Equipment & Software Services",
    aliases: { en: ["it","software","developer","programming","app","website","web","server","network","cloud","sysadmin"],
               ar: ["تقنيه","معلومات","برمجه","برمجيات","مطور","تطبيق","موقع","ويب","سيرفر","شبكه","سحابه"] } },
  { key: "manufacturing", ar: "التصنيع", en: "Manufacturing",
    aliases: { en: ["manufacturing","manufacture","production","factory"], ar: ["تصنيع","انتاج","مصنع","ماكينه"] } },
  { key: "medical", ar: "الأجهزة الطبية", en: "Medical Devices",
    aliases: { en: ["medical","medicine","doctor","clinic","hospital","health","device"], ar: ["طبي","طب","دواء","عياده","مستشفى","صحه","جهاز طبي"] } },
  { key: "security", ar: "الأمن والسلامة", en: "Security & Safety",
    aliases: { en: ["security","safety","camera","cctv","alarm","guard"], ar: ["امن","سلامه","كاميرا مراقبه","انذار","حارس","حمايه"] } },
  { key: "energy", ar: "الطاقة", en: "Energy",
    aliases: { en: ["energy","solar","battery","generator","power","fuel"], ar: ["طاقه","شمسيه","الواح","بطاريه","مولد","كهرباء","وقود","بنزين","ديزل"] } },
  { key: "services", ar: "الخدمات", en: "Services",
    aliases: { en: ["service","services","repair","fix","maintenance","cleaning","delivery","installation","plumber","electrician","handyman","cooking","catering","tutoring","translation","accounting","photography"],
               ar: ["خدمه","خدمات","صيانه","تصليح","اصلاح","تنظيف","توصيل","تركيب","سباك","كهربائي","عامل","طبخ","ضيافه","تدريس","ترجمه","محاسبه","تصوير"] } },
  { key: "home", ar: "مستلزمات المنزل", en: "Home Supplies",
    aliases: { en: ["home","household","kitchen","bathroom","appliance","appliances","cleaning"], ar: ["منزل","بيت","مطبخ","حمام","ادوات منزليه","اجهزه منزليه"] } },
  { key: "furniture", ar: "الأثاث", en: "Furniture",
    aliases: { en: ["furniture","sofa","couch","bed","table","chair","desk","wardrobe","cabinet","shelf"],
               ar: ["اثاث","كنبه","كنب","كرسي","كراسي","سرير","طاوله","مكتب","دولاب","خزانه","رف"] } },
  { key: "office", ar: "مستلزمات المكاتب والمدارس", en: "Office & School Supplies",
    aliases: { en: ["office","school","stationery","supplies","pen","pencil","notebook","paper","printer"],
               ar: ["مكتب","مدرسه","قرطاسيه","دفتر","قلم","اقلام","ورق","طابعه","مستلزمات"] } },
  { key: "apparel", ar: "الملابس والإكسسوارات", en: "Apparel & Accessories",
    aliases: { en: ["clothes","clothing","apparel","fashion","dress","shirt","accessory","accessories","bag","handbag","purse","wallet"],
               ar: ["ملابس","ازياء","موضه","فستان","قميص","اكسسوار","اكسسوارات","شنطه","حقيبه","محفظه"] } },
  { key: "beauty", ar: "النساء والجمال", en: "Women's & Beauty",
    aliases: { en: ["beauty","makeup","cosmetic","cosmetics","skincare","perfume","fragrance","hair","women","ladies","salon"],
               ar: ["جمال","مكياج","ميك اب","تجميل","عنايه","عطر","عطور","شعر","نساء","نسائي","سيدات","صالون"] } },
  { key: "environment", ar: "البيئة", en: "Environment",
    aliases: { en: ["environment","eco","green","recycle","sustainable"], ar: ["بيئه","بيئي","تدوير","اعاده تدوير","استدامه","اخضر"] } },
  // New categories added
  { key: "commodities", ar: "السلع", en: "Commodities",
    aliases: { en: ["commodity","commodities","grain","sugar","rice","oil"], ar: ["سلع","سلعه","حبوب","سكر","ارز","زيت","قمح","ذره"] } },
  // mobile_accessories and laptops_accessories were moved to the top of
  // the list (above `electronics`) so that the relevance-ranker's stable
  // tie-break favours them when they score equally with `electronics` on
  // a query like "phone" or "هاتف". See note near `mobile_accessories`.
  { key: "sports_equipment", ar: "معدات ولوازم الرياضة", en: "Sports Equipment & Supplies",
    aliases: { en: ["sport","sports","gym","fitness","football","soccer","basketball","tennis","running","cycling","weight"],
               ar: ["رياضه","رياضي","جيم","لياقه","كوره","كره قدم","كره سله","تنس","جري","ركض","دراجه","اوزان","حديد"] } },
  { key: "games_toys", ar: "الألعاب والدمى", en: "Games & Toys",
    aliases: { en: ["game","games","toy","toys","playstation","xbox","nintendo","console","puzzle","board"],
               ar: ["لعبه","العاب","دميه","دمى","بلايستيشن","اكس بوكس","نينتندو","جهاز العاب","تركيب","العاب لوحيه"] } },
  { key: "clothes_shoes", ar: "الملابس والأحذية", en: "Clothes & Shoes",
    aliases: { en: ["clothes","clothing","shoe","shoes","sneaker","sneakers","boot","sandal","fashion","dress","shirt","jacket","jeans","trouser","pant"],
               ar: ["ملابس","حذاء","احذيه","شوز","سنيكر","بوت","صندل","ازياء","فستان","قميص","جاكيت","جينز","بنطلون"] } },
  { key: "health_fitness", ar: "الصحة واللياقة", en: "Health & Fitness",
    aliases: { en: ["health","fitness","gym","workout","supplement","vitamin","wellness","yoga"],
               ar: ["صحه","لياقه","رياضه","جيم","تمرين","مكمل","فيتامين","يوغا","يوجا"] } }
];

// Get category keys as array (for backward compatibility)
export const CATEGORIES = categories.map(c => c.key);

// MVP categories for home page
// iter296 — `jobs` removed. Jobs is not a Product and not a Service;
// it requires its own dedicated "Post a Job" flow (future feature).
// Showing it in Shop while there is no posting flow creates dead
// navigation. See PRD.md backlog → "Dedicated Post-a-Job flow".
export const MVP_CATEGORIES = [
  "electronics",
  "vehicles",
  "spare_parts",
  "real_estate",
  "services",
  "energy",
  "it"
];

// iter296 — Categories that must never appear in any user-facing
// picker (Shop grid, Shop chips, product picker, service picker,
// posting chooser). Used by `isPostableCategory()`. Centralised so
// any future surface that builds a picker reads ONE list.
export const HIDDEN_FROM_PUBLIC_CATEGORIES = ["jobs"];

export function isPostableCategory(key) {
  return !!key && !HIDDEN_FROM_PUBLIC_CATEGORIES.includes(String(key).toLowerCase());
}

// Get translated category name
export function getCategoryName(key, lang = 'ar') {
  const cat = categories.find(c => c.key === key);
  if (cat) {
    return lang === 'ar' ? cat.ar : cat.en;
  }
  // Fallback: convert snake_case to Title Case
  return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Legacy key mappings for backward compatibility with old database entries
export const LEGACY_CATEGORY_MAP = {
  'electronics_accessories': 'electronics',
  'vehicles_motorbikes': 'vehicles',
  'vehicle_spare_parts': 'spare_parts',
  'fabric_raw_materials': 'fabrics',
  'it_equipment_software': 'it',
  'it_equipment_software_services': 'it',
  'pets_cattle': 'pets',
  'pets_livestock': 'pets',
  'jewellery': 'jewelry',
  'food_beverage': 'food',
  'industrial_machinery': 'industrial',
  'equipment_tools': 'equipment',
  'garden_decorations': 'garden',
  'medical_devices': 'medical',
  'security_safety': 'security',
  'home_supplies': 'home',
  'office_school_supplies': 'office',
  'apparel_accessories': 'apparel',
  'womens_beauty': 'beauty',
  'gifts_crafts': 'gifts',
  'metals_alloys': 'metals',
  'fabrics_raw_materials': 'fabrics',
  // New category mappings
  'mobile_accessories': 'mobile_accessories',
  'laptops_accessories': 'laptops_accessories',
  'sports_equipment': 'sports_equipment',
  'games_toys': 'games_toys',
  'clothes_shoes': 'clothes_shoes',
  'health_fitness': 'health_fitness',
  'commodities': 'commodities'
};

// Normalize category key (handle legacy keys)
export function normalizeCategoryKey(key) {
  if (!key) return '';
  return LEGACY_CATEGORY_MAP[key] || key;
}
